import {
  LayoutGrid, Users,
  // ★ Clock · CalendarDays · Wallet 은 판 C 에서 빠졌다 — 근태 셋이 근태 메뉴로 가면서
  //   이 트리에서 쓰는 곳이 없어졌다(타입검사가 잡았다).
  BarChart3, Network, FileCheck, FileText, UserMinus, BookMarked, Award, ClipboardCheck, GraduationCap, FilePen, Crown, Shield, SlidersHorizontal, type LucideIcon,
} from '../icons';

/**
 * 인사 메뉴 트리 — «인사» 셸(HrShell)의 좌측 칸이 그리는 목록 한 벌.
 *
 * ── 왜 이 파일이 SSOT 인가 ────────────────────────────────────────────────
 * 인사 화면들은 이미 있다 — 근태 1~6단계에서 만든 `/erp/hr/*` 열둘이 그것이고,
 * 지금까지는 **메뉴가 없어 주소로만** 들어갈 수 있었다(내 근태 화면 아래 링크 목록).
 * 이 파일은 그 화면들을 하나의 트리로 묶는 목록이며, 셸과 인사 홈(허브)이 **같은 배열**을
 * 본다 — 목록을 두 벌로 두면 한쪽만 늘어난다.
 *
 * ── 생김새 기준 ──────────────────────────────────────────────────────────
 * 통합설정(`CompanyAdmin.css` 의 `.cadm`)과 같은 규칙이다: 240px 칸 · 제목 + 검색 ·
 * 그룹 라벨 · 이름과 부제를 인 항목 · 켜지면 왼쪽 막대. 그쪽 클래스를 그대로 쓰지 않는 것은
 * `.cadm__*` 이 통합설정 화면의 것이라 "같은 이름인데 두 곳에서 다르게 보이는" 상태를
 * 만들지 않기 위해서다(`CompanyInfoScreen.css` 머리말과 같은 사정) — 이름은 `hrnav-*`.
 *
 * ⚠ 여기 없는 화면은 트리에도 없다. 시안(docs/hr-screens-sample.html)에는 인사발령·퇴사 처리·
 *   증명서 발급·인사평가 같은 화면이 더 있지만, **표도 API 도 아직 없다** — 만들어질 때
 *   이 배열에 한 줄을 더하면 트리·허브가 함께 늘어난다.
 */
export interface HrMenuItem {
  to: string;
  label: string;
  /** 항목 아래 한 줄 — "이 화면이 무엇을 정하는가". 근태 화면들이 이미 쓰던 문구를 그대로 옮겼다. */
  hint: string;
  icon: LucideIcon;
  /** 셸 밖으로 나가는 항목(트리가 사라진다) — 팀 근태처럼 인사 셸의 자식이 아닌 경로. */
  leaves?: boolean;
}

export interface HrMenuGroup {
  label: string;
  items: HrMenuItem[];
}

/** 인사 셸의 뿌리 경로. NavBar 의 레일 항목·라우터·관리자 전용 판정이 같은 값을 본다. */
export const HR_HOME_PATH = '/erp/hr';

export const HR_MENU: readonly HrMenuGroup[] = [
  {
    label: '현황',
    items: [
      { to: HR_HOME_PATH, label: '인사 홈', hint: '오늘 처리할 것', icon: LayoutGrid },
      // 인사 통계 (2026-08-27, 시안 A18) — **표도 라우트도 늘리지 않는다.** 명부·연차 현황·
      //   대표/부서장·퇴사 기록, 담당자가 이미 부르는 응답을 세로로 세운 화면이다.
      { to: '/erp/hr/stats', label: '인사 통계', hint: '인원 · 근속 · 소진율', icon: BarChart3 },
    ],
  },
  {
    label: '직원',
    items: [
      // ★★ **«직원 명부» 와 «사용자 관리» 를 한 줄로 합쳤다**(2026-08-27, 사용자 지시).
      //   둘로 갈라 놓으니 사람 하나를 넣는 데 화면을 두 번 열어야 했다 — 인사 정보는 이쪽,
      //   권한과 부서는 저쪽. 옛 두 화면은 지우지 않는다(근태 메뉴·관리 콘솔이 아직 쓴다).
      // ★ 2026-09-13 (사용자 지시 · K) — 「직원 관리」 는 **새 직원 명부**(/erp/hr/employees)로 간다. 이름은 사용자가 부르는 그대로.
      //   종전 통합 화면(/erp/hr/staff · StaffPage)은 지우지 않는다 — 계정 · 권한 · 부서 편집은 새 상세의
      //   「계정 · 권한 · 부서 관리 →」 가 `?userId=` 로 그 사람을 연 채 보낸다.
      { to: '/erp/hr/employees', label: '직원 관리', hint: '명부 · 입사 · 인사카드', icon: Users },
      { to: '/erp/hr/assignments', label: '인사발령', hint: '이동 · 승진 · 휴직', icon: Network },
      { to: '/erp/hr/offboarding', label: '퇴사 처리', hint: '체크리스트 · 인계 · 정산', icon: UserMinus },
      { to: '/erp/hr/certificates', label: '증명서 발급', hint: '재직 · 경력 · 확인 대기', icon: FileCheck },
      // 정정 요청 (2026-08-27, 시안 B2 의 짝 · migration 136) — 직원이 올린 것을 담당자가 처리한다.
      { to: '/erp/hr/profile-requests', label: '정정 요청', hint: '직원이 올린 기록 정정', icon: FilePen },
    ],
  },
  {
    label: '인사기록',
    items: [
      // 경력·학력 (2026-08-27, 시안 A19 · migration 132) — 인사기록카드의 「경력」 탭이
      //   가리키던 데이터. 인사평가·교육은 표가 생길 때 여기 한 줄씩 는다.
      { to: '/erp/hr/records/career', label: '경력 · 학력', hint: '입사 전 이력 · 학위', icon: BookMarked },
      // 포상·징계 (2026-08-27, 시안 A20 · migration 133) — 상훈과 징계를 한 목록으로.
      { to: '/erp/hr/records/awards', label: '포상 · 징계', hint: '상훈 · 징계 · 감경', icon: Award },
      // 인사평가 (2026-08-27, 시안 A21 · migration 134) — 회차 운영. 교육·자격은 표가 생길 때 는다.
      { to: '/erp/hr/evaluations', label: '인사평가', hint: '회차 · 대상 · 진행률', icon: ClipboardCheck },
      // 교육·자격·어학 (2026-08-27, 시안 A22 · migration 135) — 시안의 «인사기록» 묶음 마지막 줄.
      { to: '/erp/hr/records/training', label: '교육 · 자격 · 어학', hint: '법정교육 · 자격 · 어학', icon: GraduationCap },
    ],
  },
  /**
   * ★★ **「근태 · 휴가」 묶음을 근태 메뉴로 보냈다** (2026-08-28 · 자리 재정의 판 5).
   *
   *    입사일 관리 · 연차 관리 · 근무제 · 회사 일정 · 근태 현황 · 연장 정산 — 여섯 다
   *    **근태 화면(`/erp/attendance`)의 「근태 관리」 묶음**이 갖는다. 라우트는 그대로 산다.
   *
   *    왜 옮겼나 — 인사와 근태가 한 트리에 있으면 **근태만 맡은 사람이 인사 화면 앞에 선다.**
   *    자리를 가르는 것이 이 판의 전부다.
   */
  /**
   * ★★ **담당자도 «자기 인사» 를 본다** (2026-08-29 · 사용자 지적).
   *
   *    종전에는 트리가 **둘 중 하나**였다 — 관리자면 담당자 트리, 아니면 «내 인사». 그래서
   *    인사 담당자가 **자기 인사기록카드도 증명서 신청도 볼 수 없었다.** 남의 인사를 다루는
   *    사람이라고 해서 자기 인사가 없어지는 것이 아니다.
   * ★ 직원 트리의 것과 **같은 주소·같은 이름**이다(두 벌로 두면 한쪽만 는다).
   */
  {
    label: '내 인사',
    items: [
      { to: '/erp/hr/me/record', label: '내 인사기록카드', hint: '신상 · 발령 · 경력 · 상벌', icon: BookMarked },
      { to: '/erp/hr/me/certificates', label: '증명서 발급 신청', hint: '재직 · 경력', icon: FileText },
    ],
  },
  {
    label: '조직 · 권한',
    items: [
      // 직위·직급 마스터 (2026-08-27, migration 138) — 직위 이름과 급을 정하는 유일한 자리.
      { to: '/erp/hr/job-grades', label: '직위 · 직급', hint: '서열 — 승진으로 오른다', icon: Crown },
      // 직책 마스터 (2026-08-29, migration 144) — 직위와 **다른 축**이라 자리도 따로 둔다.
      { to: '/erp/hr/job-roles', label: '직책', hint: '보임 — 발령으로 부여한다', icon: Network },
      /**
       * ★ 부제가 「결재 사다리의 칸」이었다 — **2026-09-04 결정으로 거짓이 됐다.**
       *   결재선은 사람이 만들고 조직 사다리로 도출하지 않는다(`adr-20260904-approval-line-user-composed`).
       *   대표·부서장은 **조직의 책임자**이고 「우리 조직」 범위·업무보고 자격이 그것을 읽는다.
       */
      { to: '/erp/hr/org-leaders', label: '대표 · 부서장', hint: '조직의 책임자', icon: Network },
      /**
       * ★★ 「담당자 관리」는 **통합설정으로 옮겼다** (2026-08-30 · 판 B1).
       *    「누가 무슨 업무를 맡는가」는 회사의 뼈대라 조직도·메뉴 권한 옆이 맞다.
       *    지금 자리는 **통합설정 › 조직 › 담당자**. 옛 주소(`/erp/hr/duties`)는 살려 둔다.
       */
      /**
       * ★ 「문서 종류」는 **결재로 옮겼다** (2026-08-28 · 판 5·6) — 결재 기준을 고치러
       *   인사 트리를 여는 것은 자리가 어긋난 것이다. 지금 자리는 **전자결재 › 관리 › 문서 종류**.
       *   옛 주소(`/erp/hr/doc-types`)는 살려 둔다.
       */
      // 개인정보 열람 이력 (2026-08-28, 자리 재정의 판 3) — 「남의 개인정보를 열면 기록이
      //   남는다」의 담당자 쪽 화면. 직원 쪽 짝은 «내 인사기록카드 › 열람 기록» 이다.
      { to: '/erp/hr/access-log', label: '개인정보 열람 이력', hint: '누가 · 누구를 · 무엇을', icon: Shield },
      // 기준 설정 (2026-08-28 · 판 7) — 고용형태·퇴사 사유·증명서·포상/징계·자격 종류.
      //   ★ 직위·직급은 여기 없다(제 화면이 위에 있다) — 한 값을 두 자리에서 고치지 않는다.
      { to: '/erp/hr/codes', label: '기준 설정', hint: '고용형태 · 사유 · 종류', icon: SlidersHorizontal },
    ],
  },
];

/**
 * ★★ **「맡은 조직」 — 맡은 사람에게만 보인다** (2026-08-29 · 사용자 지적).
 *
 *    종전에는 이 묶음이 **모두에게** 떴고 화면이 「팀장만 볼 수 있습니다」로 접혔다.
 *    그런데 **할 수 없는 일을 메뉴에 세워 두면 그것이 곧 잘못된 안내**다 — 눌러 보고서야
 *    안 되는 줄 아는 자리는 없느니만 못하다. 이제 **조직 지정이 있는 사람에게만** 붙인다.
 *
 * ★ 판정은 **서버가 한다** — `GET /users/me` 의 `orgScope`(대표·부서장·팀장을 이미 있는
 *   칸에서 읽는다). 옛 서버는 그 칸을 안 주는데, 그때는 «없음» 으로 본다(있는 척하지 않는다).
 * ★ **담당자 트리에도 같은 규칙으로 붙는다** — 인사 담당자가 팀장을 겸하면 둘 다 뜬다.
 *   모자는 겹쳐 쓰는 것이라 메뉴도 겹친다.
 */
const ORG_GROUP: HrMenuGroup = {
  label: '맡은 조직',
  items: [
    // 부서원 인사정보 (2026-08-27, 시안 B10) — 범위 판정은 서버가 한다.
    { to: '/erp/hr/me/team-hr', label: '부서원 인사정보', hint: '팀장 · 팀원 신상 · 근태', icon: Users },
    /**
     * ★★ **「우리 조직」을 걷어냈다** (2026-08-30 · 사용자 지적).
     *
     *    그 화면이 이고 있던 것은 둘 — **근태 현황**과 **부서원 인사정보**다. 그런데
     *    부서원 인사정보는 **바로 위에 이미 있고**, 근태 현황은 **근태 메뉴가 갖는 것**이
     *    맞다(그 메뉴의 일이다). 그러면 「우리 조직」은 **남의 화면 둘로 가는 길**만 남는데,
     *    길만 있는 메뉴는 한 번 더 누르게 할 뿐이다.
     *
     *    → 팀장은 이제 **근태 메뉴 트리에서 바로** 근태 현황을 본다(`att-menu.ts`).
     *    ⚠ 주소 `/team` 과 화면은 **살려 둔다** — 저장해 둔 링크가 안 막히게(레일 등록은 0건).
     */
  ],
};

/**
 * 직원이 보는 «내 인사» 트리 (2026-08-26) — 담당자 트리와 **같은 껍데기에 범위만 나**로 좁힌다.
 *
 * 여기 있는 것은 전부 **지금 도는 화면**이다 — 내 근태(출퇴근)·휴가/출장/잔업 내역·전자결재.
 * 시안의 «내 인사기록카드» 는 2026-08-27 에 열었다(A19~A22 의 표가 다 생겨 채울 수 있게 됐다).
 *
 * `leaves: true` 인 둘은 인사 셸 밖의 화면이라 트리가 사라진다 — 근태·전자결재는 **제 메뉴가
 * 따로 있는** 화면이고, 여기서는 «내 것으로 가는 길» 로만 걸어 둔다.
 */
export const MY_HR_MENU: readonly HrMenuGroup[] = [
  {
    label: '내 인사',
    items: [
      { to: HR_HOME_PATH, label: '내 인사 홈', hint: '오늘 · 내 숫자', icon: LayoutGrid },
      // 내 인사기록카드 (2026-08-27, 시안 B2) — 회사가 가진 내 기록을 한 장으로. 읽기 전용이다.
      { to: '/erp/hr/me/record', label: '내 인사기록카드', hint: '신상 · 발령 · 경력 · 상벌', icon: BookMarked },
    ],
  },
  {
    label: '증명서',
    items: [
      { to: '/erp/hr/me/certificates', label: '증명서 발급 신청', hint: '재직 · 경력', icon: FileText },
    ],
  },
  {
    label: '결재',
    items: [
      // ★ 여기서 나가면 **전자결재 화면**이 뜬다(`leaves: true`). 그 화면은 여러 함을 이고
      //   있어 제목이 고른 함에 따라 바뀌므로, 우리가 그 제목을 바꿀 수는 없다 —
      //   대신 메뉴가 **어디로 가는지**를 그대로 적는다(2026-08-27).
      { to: '/approval', label: '전자결재', hint: '내 신청 · 결재 진행 · 회수', icon: FileCheck, leaves: true },
    ],
  },
];

/**
 * 「인사 담당」 — 역할은 STAFF 인데 **인사 메뉴를 명시적으로 받은 사람**의 트리에 붙는 묶음 (2026-09-14).
 *
 * ★ 서버가 이 사람에게 여는 화면만 싣는다 — 지금은 「직원 관리」(`/erp/hr/employees*` · `isHrAdminOrGranted(auth, 'hr')`).
 *   다른 담당자 화면은 라우터가 아직 역할(RoleGuard)로 막으므로 싣지 않는다 — 누르면 돌아가는 메뉴는 잘못된 안내다.
 * ★ 항목은 담당자 트리의 **그 객체**를 쓴다 — 두 벌로 부르면 한쪽만 고쳐진다.
 */
const HR_GRANT_GROUP: HrMenuGroup = {
  label: '인사 담당',
  items: HR_MENU.flatMap((g) => g.items).filter((i) => i.to === '/erp/hr/employees'),
};

/**
 * 이 사람이 볼 트리 — 회사 관리자면 담당자 트리, 아니면 «내 인사».
 *
 * ★ `hasOrg` = **맡은 조직이 있는가**(`/users/me` 의 `orgScope !== 'NONE'`).
 *   참일 때만 「맡은 조직」 묶음이 붙는다 — 담당자든 직원이든 **같은 규칙**이다.
 *   기본값이 거짓인 것은 **모르면 안 보여 주는 쪽**이 맞기 때문이다(옛 서버는 그 칸을 안 준다).
 */
export function hrMenuFor(isAdmin: boolean, hasOrg = false, employeesGranted = false): readonly HrMenuGroup[] {
  // ★ 2026-09-14 — 역할은 STAFF 인데 인사 메뉴를 받은 사람(`lib/hr-grant.ts`)은 «내 인사» 뒤에 「인사 담당」 이 붙는다.
  const base = isAdmin ? HR_MENU : employeesGranted ? [...MY_HR_MENU, HR_GRANT_GROUP] : MY_HR_MENU;
  if (!hasOrg) return base;
  // 「맡은 조직」은 «내 인사» 바로 뒤에 선다 — 내 것 다음이 내가 맡은 것이다.
  const at = base.findIndex((g) => g.label === '내 인사');
  const i = at < 0 ? base.length : at + 1;
  return [...base.slice(0, i), ORG_GROUP, ...base.slice(i)];
}

/**
 * 트리 검색 — **목록만 거른다.** 치는 도중에 오른쪽 화면이 발밑에서 바뀌면 안 된다
 * (통합설정 트리 검색과 같은 규칙). 이름과 부제를 함께 본다.
 */
export function filterHrMenu(groups: readonly HrMenuGroup[], query: string): HrMenuGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups.map((g) => ({ ...g, items: [...g.items] }));
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) => i.label.toLowerCase().includes(q) || i.hint.toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.items.length > 0);
}

/** 트리 전체를 한 줄로 편 목록 — 인사 홈(허브)이 카드로 그린다. */
export function flatHrMenu(groups: readonly HrMenuGroup[] = HR_MENU): HrMenuItem[] {
  return groups.flatMap((g) => g.items).filter((i) => i.to !== HR_HOME_PATH);
}
